import { useContext, useEffect } from 'react';
import { SegmentedControl, SegmentedControlItem, Input, Textarea, Select, Divider, Box, Link } from '@bitrise/bitkit';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';
import useStartBuildDialogData from '@/pages/App/hooks/useStartBuildDialogData';
import BranchSelector from '@/components/BranchSelector/BranchSelector';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useLicensePools from '@/pages/workspace/WorkspaceSettingsIntegrationsPage/components/LicensePools/useLicensePools';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';
import CurlCommand from '../StartBuildDialog/CurlCommand/CurlCommand';
import CustomVars from '../StartBuildDialog/CustomVars/CustomVars';
import FormBlock from '../StartBuildDialog/FormBlock/FormBlock';
import { BuildConfigurationType, StackAndMachineConfiguration } from '../StartBuildDialog/types';

const getAvailableMachines = (stackAndMachineConfig: StackAndMachineConfiguration, stackId?: string) => {
  if (!stackId || !stackAndMachineConfig.availableStacks || !stackAndMachineConfig.availableStacks[stackId]) {
    return;
  }

  const { availableMachines } = stackAndMachineConfig.availableStacks[stackId];

  return availableMachines?.map((id) => ({
    id,
    ...stackAndMachineConfig.availableMachineTypeConfigs[id],
  }));
};
export interface BuildConfigurationProps {
  app: AppDetailsPageProps['app'];
  buildTriggerToken: string;
  showcUrlCommand: boolean;
}

export const BASED_ON_TRIGGER_MAP = 'Based on trigger map';

export const getFormDataToSend = (formData: BuildConfigurationType) => {
  const { pipelineableId, ...rest } = formData;
  const formDataToSend: BuildConfigurationType = rest;
  if (pipelineableId !== BASED_ON_TRIGGER_MAP) {
    formDataToSend.pipelineableId = pipelineableId;
  }
  return formDataToSend;
};

const BuildConfiguration = (props: BuildConfigurationProps) => {
  const { app, buildTriggerToken, showcUrlCommand } = props;

  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { control, setValue, watch } = useFormContext<BuildConfigurationType>();

  const formData = watch();
  const buildConfigType = watch('buildConfigType');

  const { data: licensePoolsData, isLoading: isLicensePoolsLoading } = useLicensePools(account.slug, account.isOwner);

  const handleSourceChange = () => {
    setValue('branch', '');
    setValue('commitHash', '');
    setValue('tag', '');
  };

  const DEBOUNCE_TIME = app.usesRepositoryYml ? 500 : 0;

  const [debouncedBranch, setDebouncedBranch] = useDebounceValue(formData.branch, DEBOUNCE_TIME);
  const [debouncedCommitHash, setDebouncedCommitHash] = useDebounceValue(formData.commitHash, DEBOUNCE_TIME);
  const [debouncedTag, setDebouncedTag] = useDebounceValue(formData.tag, DEBOUNCE_TIME);

  let params;

  if (app.usesRepositoryYml) {
    params = {
      branch: '',
      commit_hash: '',
      tag: '',
    };

    if (buildConfigType === 'basic' || formData.source === 'branch') {
      params.branch = debouncedBranch || '';
    }
    if (buildConfigType === 'advanced') {
      if (formData.source === 'commitHash') {
        params.commit_hash = debouncedCommitHash || '';
      }
      if (formData.source === 'tag') {
        params.tag = debouncedTag || '';
      }
    }
  }

  const {
    hasTriggerMap,
    isLoading: isStartBuildDialogDataLoading,
    stackAndMachineConfiguration,
    pipelines,
    workflows,
  } = useStartBuildDialogData({
    appSlug: app.slug,
    params,
  });

  const availableMachines = stackAndMachineConfiguration
    ? getAvailableMachines(stackAndMachineConfiguration, formData.stack)
    : [];

  useEffect(() => {
    let defaultPipelineOrWorkflow = '';
    if (hasTriggerMap) {
      defaultPipelineOrWorkflow = BASED_ON_TRIGGER_MAP;
    } else {
      if (!workflows?.length && pipelines?.length === 1) {
        defaultPipelineOrWorkflow = `pipeline#${pipelines[0]}`;
      }
      if (!pipelines?.length && workflows?.length === 1) {
        defaultPipelineOrWorkflow = `workflow#${workflows[0]}`;
      }
    }
    if (!formData.pipelineableId) {
      setValue('pipelineableId', defaultPipelineOrWorkflow);
    }
  }, [formData.pipelineableId, hasTriggerMap, pipelines, setValue, workflows]);

  return (
    <>
      <SegmentedControl
        marginBlockEnd="16"
        onChange={(value: 'basic' | 'advanced') => {
          setValue('buildConfigType', value);
        }}
        value={buildConfigType}
      >
        <SegmentedControlItem value="basic">Basic</SegmentedControlItem>
        <SegmentedControlItem value="advanced">Advanced</SegmentedControlItem>
      </SegmentedControl>
      <FormBlock isLastItem={buildConfigType === 'basic'}>
        {buildConfigType === 'basic' && (
          <Controller
            control={control}
            name="branch"
            render={({ field }) => (
              <BranchSelector
                isQueryEnabled
                label="Source branch"
                isRequired
                selectedBranch={field.value}
                onSelect={(selectedBranchName: string) => {
                  field.onChange(selectedBranchName);
                  setDebouncedBranch(selectedBranchName);
                }}
                slug={app.slug}
              />
            )}
          />
        )}
        {buildConfigType === 'advanced' && (
          <Box display="flex" gap="8" alignItems="flex-end">
            <Controller
              control={control}
              name="source"
              render={({ field }) => (
                <Select
                  label="Source"
                  isRequired
                  width="212px"
                  onChange={(e) => {
                    field.onChange(e);
                    handleSourceChange();
                  }}
                  value={field.value}
                >
                  <option value="branch">Branch</option>
                  <option value="tag">Tag</option>
                  <option value="commitHash">Commit hash</option>
                </Select>
              )}
            />
            {formData.source === 'branch' && (
              <Controller
                control={control}
                name="branch"
                render={({ field }) => (
                  <BranchSelector
                    isQueryEnabled
                    label=""
                    selectedBranch={field.value}
                    onSelect={field.onChange}
                    slug={app.slug}
                    alignItems="flex-end"
                  />
                )}
              />
            )}
            {formData.source === 'tag' && (
              <Controller
                control={control}
                name="tag"
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Input
                    inputRef={ref}
                    placeholder="e.g. 1.0.0"
                    width="100%"
                    onChange={(event) => {
                      onChange(event);
                      setDebouncedTag(event.target.value);
                    }}
                    {...fieldProps}
                  />
                )}
              />
            )}
            {formData.source === 'commitHash' && (
              <Controller
                control={control}
                name="commitHash"
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Input
                    inputRef={ref}
                    placeholder="e.g. 3ce00ffa28e7b357fcaf67bcfa74e14566ed92cf"
                    width="100%"
                    onChange={(event) => {
                      onChange(event);
                      setDebouncedCommitHash(event.target.value);
                    }}
                    {...fieldProps}
                  />
                )}
              />
            )}
          </Box>
        )}
        <Controller
          control={control}
          name="pipelineableId"
          render={({ field }) => (
            <Select
              isRequired
              label="Target"
              placeholder="Select Workflow or Pipeline"
              isDisabled={isStartBuildDialogDataLoading || (workflows.length === 0 && pipelines.length === 0)}
              errorText={
                !isStartBuildDialogDataLoading && workflows.length === 0 && pipelines.length === 0
                  ? 'There are no targets on the selected source'
                  : ''
              }
              {...field}
            >
              {hasTriggerMap && <option>{BASED_ON_TRIGGER_MAP}</option>}
              {pipelines && pipelines.length > 0 && (
                <optgroup label="Pipelines">
                  {pipelines.map((p) => (
                    <option key={p} value={`pipeline#${p}`}>
                      {p}
                    </option>
                  ))}
                </optgroup>
              )}
              {workflows && workflows.length > 0 && (
                <optgroup label="Workflows">
                  {workflows.map((w) => (
                    <option key={w} value={`workflow#${w}`}>
                      {w}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>
          )}
        />
        <Divider />
        <Controller
          control={control}
          name="commitMessage"
          render={({ field }) => (
            <Textarea label="Message" placeholder="e.g. Triggered by Bitrise build trigger URL." {...field} />
          )}
        />
        {buildConfigType === 'advanced' && stackAndMachineConfiguration && (
          <>
            <Controller
              control={control}
              name="stack"
              render={({ field }) => (
                <Select label="Stack" {...field}>
                  <option value="">Default, based on Workflow settings</option>
                  {Object.keys(stackAndMachineConfiguration.availableStacks).map((id) => (
                    <option key={id} value={id}>
                      {stackAndMachineConfiguration.availableStacks[id].title}
                    </option>
                  ))}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="machineTypeId"
              render={({ field }) => (
                <Select helperText="You can select a machine type based on the stack" label="Machine type" {...field}>
                  <option value="">Default, based on Workflow settings</option>
                  {availableMachines &&
                    availableMachines.length > 0 &&
                    availableMachines.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                </Select>
              )}
            />
            {account.isOwner && !isLicensePoolsLoading && licensePoolsData && licensePoolsData.length > 0 && (
              <Controller
                control={control}
                name="licensePoolId"
                render={({ field }) => (
                  <Select
                    helperText="You can run your builds on Bitrise machines relying on license keys."
                    label="License pool"
                    placeholder="Select License pool"
                    {...field}
                  >
                    {licensePoolsData.map(({ name, id }) => (
                      <option key={name} value={id}>
                        {name}
                      </option>
                    ))}
                  </Select>
                )}
              />
            )}
          </>
        )}
        {buildConfigType === 'advanced' && (
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <Input
                type="number"
                label="Priority"
                helperText={
                  <>
                    Set priority between -100 and +100. Default value is 0. Available on certain plans only.{' '}
                    <Link
                      colorScheme="purple"
                      href="https://devcenter.bitrise.io/en/builds/configuring-build-settings/build-priority.html"
                      isExternal
                    >
                      Learn more
                    </Link>
                  </>
                }
                {...field}
              />
            )}
          />
        )}
      </FormBlock>
      {buildConfigType === 'advanced' && (
        <>
          <CustomVars isLastItem={!showcUrlCommand} />
          {showcUrlCommand && (
            <CurlCommand buildTriggerToken={buildTriggerToken} data={getFormDataToSend(formData)} slug={app.slug} />
          )}
        </>
      )}
    </>
  );
};

export default BuildConfiguration;
